import { onMounted } from "vue";
import { f7 } from "framework7-vue";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { useKeycloakStore } from "../pinia-stores/keycloak.js";
import { setIntentUrl } from "./useSessionGuard.js";

const MOBILE_URL_SCHEME =
  import.meta.env.VITE_AVOCASH_MOBILE_URL_SCHEME || "avocash://login";

export function useHandleLink() {
  console.log("useHandleLink initialized");
  function normalizeLaunchUrl(rawUrl) {
    console.log("normalizeLaunchUrl called with rawUrl:", rawUrl);
    if (!rawUrl) return "";
    if (typeof rawUrl === "string") return rawUrl.trim();
    if (typeof rawUrl === "object" && typeof rawUrl.url === "string") {
      return rawUrl.url.trim();
    }
    return "";
  }

  function resolveNavigatePath(rawUrl) {
    console.log("resolveNavigatePath called with rawUrl:", rawUrl);
    const urlString = normalizeLaunchUrl(rawUrl);
    if (!urlString) return "";

    try {
      const url = new URL(urlString);
      const currentOrigin = window.location.origin || "";
      let navigateUrl = "";

      if (url.toString().startsWith("avocash://login")) {
        navigateUrl = url.toString().split("avocash://login").pop();
      } else if (url.toString().startsWith("https://avocash.com")) {
        navigateUrl = url.toString().split("https://avocash.com").pop();
      } else if (currentOrigin && url.toString().startsWith(currentOrigin)) {
        navigateUrl = url.toString().split(currentOrigin).pop();
      }

      if (!navigateUrl || navigateUrl === "/" || navigateUrl.startsWith("?")) {
        return "";
      }

      return navigateUrl;
    } catch (error) {
      console.warn("Unable to resolve app launch URL:", rawUrl, error);
      return "";
    }
  }

  function handleExternalLink(url0) {
    if (Capacitor.isNativePlatform()) {
      const normalizedUrl = normalizeLaunchUrl(url0);
      if (!normalizedUrl) return;

      const url = new URL(normalizedUrl);
      console.log("handleExternalLink url", url);
      const { pathname, protocol, hostname, origin } = url;
      const isAvocashUrl =
        origin === import.meta.env.VITE_AVOCASH_SITE_URL ||
        protocol === "avocash:" ||
        hostname === "login" ||
        origin === "http://localhost:5173";
      const pathUrlList = ["/links", "/login", "/share", "/invite", "/pay"];
      const isSpecialPath = pathUrlList.some((segment) =>
        pathname.startsWith(segment)
      );
      const urlString = url.toString();
      console.log("handleExternalLink urlString", urlString);
      let navigateUrl;
      if (!isAvocashUrl) {
        Browser.open({ url });
      } else if (isSpecialPath) {
        const segment = pathUrlList.find((segment) =>
          pathname.startsWith(segment)
        );
        console.log("handleExternalLink segment", segment);
        navigateUrl = urlString.split(segment).pop().startsWith("/")
          ? urlString.split(segment).pop()
          : `/${urlString.split(segment).pop()}`;
        console.log("handleExternalLink navigateUrl", navigateUrl);
      } else {
        const originUrl = url.origin;
        console.log("handleExternalLink origin", originUrl);
        navigateUrl = urlString.split(originUrl).pop().startsWith("/")
          ? urlString.split(originUrl).pop()
          : `/${urlString.split(originUrl).pop()}`;
        console.log("handleExternalLink navigateUrl1", navigateUrl);
      }
      if (navigateUrl) {
        keycloakRedirect(navigateUrl);
      }
    }
  }

  function keycloakRedirect(navigateUrl) {
    const keycloakStore = useKeycloakStore();
    if (!keycloakStore) return;
    if (Capacitor.isNativePlatform()) {
      setIntentUrl(navigateUrl);
    }
    if (!keycloakStore.authenticated) {
      keycloakStore.login({
        redirectUri: `${MOBILE_URL_SCHEME}${navigateUrl}`,
      });
    } else {
      f7.views.main.router.navigate(navigateUrl);
    }
  }

  function isOAuthCallbackUrl(rawUrl) {
    const urlString = normalizeLaunchUrl(rawUrl);
    if (!urlString) return false;
    if (!urlString.toLowerCase().startsWith("avocash://login")) return false;
    try {
      const url = new URL(urlString);
      return (
        url.searchParams.has("state") &&
        (url.searchParams.has("code") || url.searchParams.has("error"))
      );
    } catch (error) {
      return false;
    }
  }

  function processLaunchUrl(rawUrl) {
    if (isOAuthCallbackUrl(rawUrl)) {
      console.log("Skipping Keycloak OAuth callback URL:", rawUrl);
      return true;
    }

    const navigateUrl = resolveNavigatePath(rawUrl);
    if (!navigateUrl || navigateUrl.includes("?error=login_required")) {
      return false;
    }

    console.log("Capacitor navigateUrl is processed", navigateUrl);

    const keycloakStore = useKeycloakStore();
    if (keycloakStore) {
      if (Capacitor.isNativePlatform()) {
        setIntentUrl(navigateUrl);
      }
      if (!keycloakStore.authenticated) {
        keycloakStore.login({
          redirectUri: `${MOBILE_URL_SCHEME}${navigateUrl}`,
        });
      } else {
        f7.views.main.router.navigate(navigateUrl);
      }
      return true;
    }

    return false;
  }

  onMounted(() => {
    App.addListener("appUrlOpen", async (data) => {
      try {
        const rawUrl = normalizeLaunchUrl(data?.url ?? data);
        console.log("App opened with URL:", rawUrl);

        if (!rawUrl) return;

        const wasHandled = processLaunchUrl(rawUrl);
        if (!wasHandled) {
          handleExternalLink(rawUrl);
        }
      } catch (e) {
        console.error("Invalid URL received in appUrlOpen", data?.url, e);
      }
    });

    App.getLaunchUrl().then((result) => {
      const launchUrl = normalizeLaunchUrl(result);
      console.log("App launched with URL:", launchUrl);

      if (launchUrl) {
        processLaunchUrl(launchUrl);
      }
    });

    App.addListener("appStateChange", ({ isActive }) => {
      console.log("App state changed. Is active?", isActive);
    });
    App.getState().then((state) => {
      console.log("App state:", state);
    });
    App.addListener("pause", () => {
      console.log("App paused");
    });
    App.addListener("resume", () => {
      console.log("App resumed");
    });
    App.addListener("backButton", () => {
      console.log("Back button pressed");
    });
    if (Capacitor.isNativePlatform()) {
      App.addListener("appMinimize", () => {
        console.log("App minimized");
      });
      App.addListener("appExit", () => {
        console.log("App exited");
      });
      App.getInfo().then((info) => {
        console.log("App info:", info);
      });
    }
  });

  return {
    handleExternalLink,
    keycloakRedirect,
  };
}
